import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/providers/ToastProvider';
import { PartnerDetail } from '../components/PartnerDetail';
import {
  useAdminPartner,
  useApprovePartner,
  useRejectPartner,
  useSuspendPartner,
  useChangePartnerLevel,
} from '../hooks/useAdminPartners';
import { adminPartnerService } from '../services/admin-partner.service';
import type { PartnerLevel } from '../types/admin-partner';

export default function AdminPartnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: partner, isLoading } = useAdminPartner(id!);

  const { data: commissionsData } = useQuery({
    queryKey: ['admin', 'partner', id, 'commissions'],
    queryFn: () => adminPartnerService.getCommissions({ partnerId: id, limit: 10 }),
    enabled: !!id,
  });

  const { data: payoutsData } = useQuery({
    queryKey: ['admin', 'partner', id, 'payouts'],
    queryFn: () => adminPartnerService.getPayoutBatches({ limit: 100 }),
    enabled: !!id,
  });

  const approveMutation = useApprovePartner();
  const rejectMutation = useRejectPartner();
  const suspendMutation = useSuspendPartner();
  const changeLevelMutation = useChangePartnerLevel();

  const handleApprove = async () => {
    try {
      await approveMutation.mutateAsync(id!);
      queryClient.invalidateQueries({ queryKey: ['admin', 'partners', id] });
      toast({ title: 'Parceiro aprovado com sucesso!', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao aprovar parceiro', variant: 'error' });
    }
  };

  const handleReject = async () => {
    try {
      await rejectMutation.mutateAsync({ id: id! });
      queryClient.invalidateQueries({ queryKey: ['admin', 'partners', id] });
      toast({ title: 'Parceiro rejeitado', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao rejeitar parceiro', variant: 'error' });
    }
  };

  const handleSuspend = async () => {
    try {
      await suspendMutation.mutateAsync({ id: id! });
      queryClient.invalidateQueries({ queryKey: ['admin', 'partners', id] });
      toast({ title: 'Parceiro suspenso', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao suspender parceiro', variant: 'error' });
    }
  };

  const handleApproveKYC = async () => {
    try {
      await adminPartnerService.updatePartnerKYC(id!, {});
      queryClient.invalidateQueries({ queryKey: ['admin', 'partners', id] });
      toast({ title: 'KYC aprovado!', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao aprovar KYC', variant: 'error' });
    }
  };

  const handleChangeLevel = async (level: PartnerLevel) => {
    try {
      await changeLevelMutation.mutateAsync({ id: id!, level });
      queryClient.invalidateQueries({ queryKey: ['admin', 'partners', id] });
      toast({ title: 'Nível alterado com sucesso!', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao alterar nível', variant: 'error' });
    }
  };

  if (!partner) {
    return null;
  }

  const commissions = commissionsData?.data || (commissionsData as any)?.items || [];
  const payoutBatches = payoutsData?.data || (payoutsData as any)?.items || [];
  const payouts = payoutBatches.flatMap((batch: any) => batch.payouts || []);

  return (
    <PartnerDetail
      partner={partner}
      commissions={commissions}
      payouts={payouts}
      isLoading={isLoading}
      onApprove={handleApprove}
      onReject={handleReject}
      onSuspend={handleSuspend}
      onApproveKYC={handleApproveKYC}
      onChangeLevel={handleChangeLevel}
      isApproving={approveMutation.isPending}
      isRejecting={rejectMutation.isPending}
      isSuspending={suspendMutation.isPending}
      isApprovingKYC={false}
      isChangingLevel={changeLevelMutation.isPending}
    />
  );
}
